import styled from '@emotion/styled';
import {Tooltip, TooltipProps} from '@mui/material';
import React, {
  Children,
  cloneElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';

/**
 * Only shows a tooltip when the text gets too long and will be truncated.
 */
export const EDynamicTooltip = ({
  title,
  children,
  ...props
}: TooltipProps): React.ReactElement => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [resizeObserver, setResizeObserver] = useState<ResizeObserver>();

  const timeoutId = useRef<number>(undefined);
  const tooltipRef = useRef<HTMLElement>(null);

  useMountEffect(() => {
    let unmounted = false;
    const ro = new ResizeObserver(() => {
      window.clearTimeout(timeoutId.current);
      timeoutId.current = window.setTimeout(() => {
        const truncated = tooltipRef.current
          ? tooltipRef.current.scrollWidth > tooltipRef.current.clientWidth
          : false;
        if (!unmounted) {
          setIsTruncated(truncated);
        }
      }, 500);
    });
    setResizeObserver(ro);

    return () => {
      unmounted = true;
      ro.disconnect();
    };
  });

  useEffect(() => {
    if (resizeObserver && tooltipRef.current) {
      resizeObserver.observe(tooltipRef.current);
    }
  }, [resizeObserver, tooltipRef]);

  return (
    <ETooltip {...props} title={isTruncated ? title : undefined}>
      {Children.count(children) > 1 ? (
        <span ref={tooltipRef}>{children}</span>
      ) : (
        cloneElement(
          children as React.ReactElement<{ref?: React.Ref<HTMLElement>}>,
          {ref: tooltipRef}
        )
      )}
    </ETooltip>
  );
};

/** The Content of the Tooltip is defined in the title Prop.
 * title='' -> does not show a tooltip
 **/
export const ETooltip = styled(
  ({className, arrow = true, placement = 'top', ...props}: TooltipProps) => (
    <Tooltip
      arrow={arrow}
      placement={placement}
      {...props}
      componentsProps={{tooltip: {className}, arrow: {className}}}
    />
  )
)`
  background-color: var(--color-core-primary);
  border-radius: 0;
  font-weight: var(--tooltip-font-weight);
  font-size: var(--font-size-default);
  white-space: pre-line;

  & .MuiTooltip-arrow {
    color: var(--color-core-primary);
    background-color: transparent;
  }
`;
